import { useState } from 'react';
import './ForceRecognizeToggle.css';

/**
 * A8r Slice D — GM force-recognize override (3-state segmented control).
 *
 * Replaces Slice C's 2-state flip-flop button with a WAI-ARIA `radiogroup`
 * of three buttons:
 *
 *   ┌───────────┬───────────────┬───────────────┐
 *   │  Natural  │ Force Recog'd │ Force Unknown │
 *   └───────────┴───────────────┴───────────────┘
 *
 *   - Natural  → setOverride(npcId, null)  — clear GM override; restore raw
 *   - Force Recog'd  → setOverride(npcId, true)
 *   - Force Unknown  → setOverride(npcId, false)
 *
 * Active state is computed from props:
 *   - `manuallyOverridden = false` → Natural is active (regardless of recognized)
 *   - `manuallyOverridden = true && recognized = true` → Force Identified active
 *   - `manuallyOverridden = true && recognized = false` → Force Unknown active
 *
 * Clicking the already-active button is a no-op (no POST fires). All three
 * buttons are disabled while a POST is in flight to prevent double-fire.
 *
 * A11y: WAI-ARIA radiogroup with aria-checked per option + aria-label on
 * each button for screen readers.
 */
export interface ForceRecognizeToggleProps {
  npcId: string;
  npcName: string;
  /** Current EFFECTIVE recognized state (already-resolved via the
   *  hook's gm_override ?? recognized logic). */
  recognized: boolean;
  /** A8r Slice D: true when gm_override is currently NOT NULL for this
   *  NPC + scenario. Drives which segmented-control button is "active". */
  manuallyOverridden: boolean;
  /** Hook method that POSTs to /fame/override. Slice D accepts `null` to
   *  clear the override (restore raw natural roll outcome). */
  setOverride: (npcId: string, recognized: boolean | null) => Promise<void>;
}

type SegmentValue = 'natural' | 'yes' | 'no';

function activeSegment(recognized: boolean, manuallyOverridden: boolean): SegmentValue {
  if (!manuallyOverridden) return 'natural';
  return recognized ? 'yes' : 'no';
}

const SEGMENT_TO_OVERRIDE: Record<SegmentValue, boolean | null> = {
  natural: null,
  yes: true,
  no: false,
};

export function ForceRecognizeToggle({
  npcId,
  npcName,
  recognized,
  manuallyOverridden,
  setOverride,
}: ForceRecognizeToggleProps) {
  const [pending, setPending] = useState<boolean>(false);
  const active = activeSegment(recognized, manuallyOverridden);

  const handleSegmentClick = (segment: SegmentValue) => {
    if (pending) return;
    if (segment === active) return; // no-op on already-active
    setPending(true);
    void Promise.resolve(setOverride(npcId, SEGMENT_TO_OVERRIDE[segment])).finally(
      () => {
        setPending(false);
      },
    );
  };

  const renderSegment = (
    segment: SegmentValue,
    label: string,
    ariaLabel: string,
    testid: string,
  ) => (
    <button
      type="button"
      role="radio"
      className={
        active === segment
          ? `br-force-recognize-segment br-force-recognize-segment-active br-force-recognize-${segment}`
          : `br-force-recognize-segment br-force-recognize-${segment}`
      }
      data-testid={testid}
      disabled={pending}
      aria-checked={active === segment}
      aria-label={ariaLabel}
      onClick={() => handleSegmentClick(segment)}
    >
      {label}
    </button>
  );

  return (
    <div
      className="br-force-recognize-row"
      data-testid={`br-force-recognize-row-${npcId}`}
    >
      <span className="br-force-recognize-name">{npcName}</span>
      <div
        className="br-force-recognize-group"
        role="radiogroup"
        aria-label={`Recognition override for ${npcName}`}
        data-testid={`br-force-recognize-group-${npcId}`}
      >
        {renderSegment(
          'natural',
          'Natural',
          `Set ${npcName} recognition to natural (no GM override)`,
          `br-force-recognize-natural-${npcId}`,
        )}
        {renderSegment(
          'yes',
          'Force Recog’d',
          `Force ${npcName} to Identified`,
          `br-force-recognize-yes-${npcId}`,
        )}
        {renderSegment(
          'no',
          'Force Unknown',
          `Force ${npcName} to Unknown`,
          `br-force-recognize-no-${npcId}`,
        )}
      </div>
    </div>
  );
}

export default ForceRecognizeToggle;
